import { HeaderLeftTabs, QueryParam } from 'utils/constants';
import { getValueFromQueryParam } from '@xto10x/core/lib/app.utils';
import { isHeaderLeftTabs } from 'utils/app.utils';
import { ObjectiveStatusLabelColorMapping } from '../../Growth/components/Objective/constants';

// Helper functions
export const capitalize = str =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const transformDemographicSummaryResponse = demographic_view => {
  const labels = [];
  const statusMap = {};
  const totals = [];
  const demographicNames = [];

  // Step 1: Aggregate data
  demographic_view?.demographic_segments.forEach(segment => {
    const demographic = segment.demographic_value || 'NA';
    const filterValues = segment.filter_values;
    const total = Object.values(filterValues).reduce(
      (sum, val) => sum + val,
      0
    );
    const label = `${capitalize(demographic)} (${total})`;
    labels.push(label);
    totals.push(total);
    demographicNames.push(demographic);

    Object.entries(filterValues).forEach(([status, count]) => {
      if (!statusMap[status]) {
        statusMap[status] = {
          label: ObjectiveStatusLabelColorMapping[status].label,
          backgroundColor: ObjectiveStatusLabelColorMapping[status].graphColor,
          data: [],
          rawValue: [],
          total: [],
          borderRadius: {
            topLeft: 8,
            topRight: 8,
            bottomLeft: 0,
            bottomRight: 0
          },
          borderSkipped: false,
          minBarLength: 15
        };
      }

      const percentage = ((count / total) * 100).toFixed(1);
      statusMap[status].data.push(parseFloat(percentage));
      statusMap[status].rawValue.push(count);
      statusMap[status].total.push(total);
    });
  });

  return {
    labels,
    demographicNames,
    datasets: Object.values(statusMap),
    selectedDemographic: demographic_view?.demographic,
    filterType: demographic_view?.filter_type,
    demographicsApplicable: demographic_view?.demographics_applicable
  };
};

/**
 * Translates PeopleCues “objective_summary” payload into
 * the six-card chart configuration required by the UI.
 *
 * @param {object} payload – The raw API response that contains `objective_summary`
 * @returns {Array<object>} – Chart-ready array of objects
 */
export const transformDemographicObjectiveSummary = payload => {
  const summary = payload.objective_summary ?? {};

  /** Utility for safe gets */
  const get = (obj, key) => (obj && obj[key]) || 0;

  /* ---------- 1. Objective state ---------- */
  const status = summary.objective_status ?? {};
  const objectiveState = {
    type: 'objective-state',
    data: {
      labels: ['ACCEPTED', 'NEGOTIATION', 'REJECTED'],
      datasets: [
        {
          label: 'Objective state',
          data: [
            get(status, 'ACCEPTED'),
            get(status, 'NEGOTIATION'),
            get(status, 'REJECTED')
          ]
        }
      ]
    }
  };

  /* ---------- 2. Objective count by type ---------- */
  const countByType = summary.objective_count_by_type ?? {};
  const companyCount = get(countByType, 'COMPANY');
  const nonAlignedCount = Object.entries(countByType).reduce(
    (acc, [k, v]) => (k === 'COMPANY' ? acc : acc + v),
    0
  );
  const objectiveCountByType = {
    type: 'objective-count-by-type',
    data: {
      labels: ['Company Objective', 'Non Aligned Objective'],
      datasets: [
        {
          label: 'Objective by type',
          data: [companyCount, nonAlignedCount]
        }
      ]
    }
  };

  /* ---------- 3. Objective last-updated details ---------- */
  const lastUpd = summary.objective_last_updated_details ?? {};
  const notUpdated = get(lastUpd.counts ?? {}, 'OBJECTIVE_NOT_UPDATED');
  const totalObjectives = companyCount + nonAlignedCount;
  const updated = Math.max(totalObjectives - notUpdated, 0);
  const objectiveLastUpdated = {
    type: 'objective-last-updated-details',
    data: {
      labels: ['OBJECTIVE UPDATED', 'OBJECTIVE NOT UPDATED'],
      datasets: [
        {
          label: `Objective not updated in last ${get(
            lastUpd,
            'objective_check_in_days'
          )} days`,
          data: [updated, notUpdated]
        }
      ]
    }
  };

  /* ---------- 4. Objective completion state ---------- */
  const completion = summary.objective_completion_status ?? {};
  const completionLabels = Object.keys(ObjectiveStatusLabelColorMapping);
  const objectiveCompletion = {
    type: 'objective-completion-state',
    data: {
      labels: completionLabels,
      datasets: [
        {
          label: 'Objective Completion State',
          data: completionLabels.map(lbl => get(completion, lbl))
        }
      ]
    }
  };

  /* ---------- 5. Objective progress buckets ---------- */
  const progressBuckets = summary.objective_progress_details?.buckets ?? [];
  const bucketIndex = new Map(progressBuckets.map(b => [b.start, b.count]));
  const orderedStarts = ['<', '0', '21', '41', '61', '81', '>'];
  const objectiveProgress = {
    type: 'objective-progress-buckets',
    data: {
      labels: ['< 0', '0-20', '21-40', '41-60', '61-80', '81-100', '> 100'],
      datasets: [
        {
          label: 'Progress',
          data: orderedStarts.map(s => bucketIndex.get(s) || 0)
        }
      ]
    }
  };

  /* ---------- 6. Manager approval status ---------- */
  const approval = summary.objective_manager_approval_status ?? {};
  const pending = get(approval, 'PENDING');
  const totalApproval = Object.values(approval).reduce((a, b) => a + b, 0);
  const notPending = totalApproval - pending;
  const managerApproval = {
    type: 'objective-manager-approval-status',
    data: {
      labels: ['NOT_PENDING', 'PENDING'],
      datasets: [
        {
          label: 'Objective By Manager Approval Status',
          data: [notPending, pending]
        }
      ]
    }
  };

  /* ---------- Final assembled array ---------- */
  return {
    objective_summary: [
      objectiveState,
      objectiveCountByType,
      objectiveLastUpdated,
      objectiveCompletion,
      objectiveProgress,
      managerApproval
    ],
    demographic_view: transformDemographicSummaryResponse(
      payload.demographic_view
    )
  };
};

export const getFiltersRequestBodyFromFilterParams = (
  selectedTeam,
  searchParams,
  userInfo,
  filters
) => {
  const params = {
    user_id:
      selectedTeam.tag_id === HeaderLeftTabs[0].tag_id &&
      !(searchParams && searchParams.searchActive)
        ? userInfo.user_reference_id
        : filters.owners?.length
        ? filters.owners.join()
        : null,
    team_id:
      selectedTeam &&
      !isHeaderLeftTabs(selectedTeam.tag_id) &&
      !(searchParams && searchParams.searchActive)
        ? selectedTeam.tag_id
        : filters.teams?.length
        ? filters.teams.join()
        : null,
    tag_id: filters.tags?.length ? filters.tags.join() : null,
    status: filters.status?.length ? filters.status.join() : null,
    objective_type: filters.objective_type,
    okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
    department: filters.dp,
    label_ids: filters.label_ids?.length ? filters.label_ids.join() : null,
    user_hierarchy_type:
      selectedTeam.tag_id === HeaderLeftTabs[1].tag_id
        ? 'DIRECT_REPORTS'
        : null,
    due_date_by: filters.due_date,
    objective_status:
      (filters.negotiation_states ? filters.negotiation_states.join() : null) ||
      (filters.objective_status ? filters.objective_status.join() : null),
    team_id_relation: filters.isTeamIntersection ? 'INTERSECTION' : 'UNION',
    tag_id_relation: filters.isTagIntersection ? 'INTERSECTION' : 'UNION',
    progress_higher_than: filters.progress_higher_than,
    progress_lower_than: filters.progress_lower_than,
    objectives_not_updated_from_days: filters.objectives_not_updated_from_days,
    search_query:
      searchParams && searchParams.searchTerm ? searchParams.searchTerm : null,
    level: filters.level,
    exclude_if_due_date_passed: !!filters.exclude_if_due_date_passed,
    manager_approval_status: filters.manager_approval_status?.length
      ? filters.manager_approval_status.join()
      : null,
    include_only_contributing_objectives: filters.contributor_filter || false,
    demographic_key: filters.demographic_key || null,
    demographic_value: filters.demographic_value || null
  };
  if (filters.objective_type)
    params.objective_type = Array.isArray(filters.objective_type)
      ? filters.objective_type.join(', ')
      : filters.objective_type;
  if (filters.level) params.level_filter_type = 'SPECIFIC_LEVEL';
  return params;
};
